import React from 'react';
import { getFormattedName } from '@utilities/commonFunction';
import { getDateFromApiDateTime } from '@calendar/utilities';
import { useSurgeons } from '@utilities/hooks/useSurgeons/useSurgeons';
import { useGetTimeZoneByHospital } from '@utilities/hooks/useGetTimeZoneByHospital/useGetTimeZoneByHospital';
import { ReleaseCard } from '@components/BlockRelease/BlockReleaseRequest/BlockReleaseRequestPage/ReleasesSection/ReleaseCard/ReleaseCard';
import { useBlockReleaseRequestContext } from '@components/BlockRelease/BlockReleaseRequest/BlockReleaseRequestContext/BlockReleaseRequestContext';
import { useHistory } from 'react-router-dom';
import { ROUTE_CONSTANTS } from '@utilities/RouteConstants';
import { BlockReleaseApiResponse } from '@data/blockReleases/types/BlockRelease.types';
import { makeStyles } from '@material-ui/core/styles';
import { createStyles } from '@material-ui/core';
import { BlockReleaseState } from '@data/blockReleases/types/BlockReleaseState';
import {
	useGetFilteredReleases
} from '@components/BlockRelease/utils/hooks/useGetFilteredReleases/useGetFilteredReleases';
import { useRouteParams } from '@components/BlockRelease/utils/hooks/useRouteParams/useRouteParams';

const useStyles = makeStyles(() =>
	createStyles({
		recommendedCard: {
			cursor: 'pointer',
		},
	}),
);

export const RecommendedReleases = () => {
	const classes = useStyles();

	const { surgeonByNpi } = useSurgeons();
	const timeZoneByHospital = useGetTimeZoneByHospital();

	const { recommendedReleases } = useGetFilteredReleases();
	const { selectedReleaseInfo } = useBlockReleaseRequestContext();
	const { recommendationId } = useRouteParams();
	const releaseId = recommendationId || selectedReleaseInfo?.selectedRelease?.id;
	const history = useHistory();

	const handleReleaseCardClick = React.useCallback((release: BlockReleaseApiResponse) => {
		const { id, npis, unit, startDateTime, state } = release;
		const nudgeQueryParam = new URLSearchParams({ surgeon: npis[0].toString() }).toString();
		const route = `${ROUTE_CONSTANTS.BLOCK_RELEASE_RECOMMENDATION_LIST}/${id}`;

		if (state === BlockReleaseState.NOT_RELEASED) {
			const manualQueryParam = new URLSearchParams({ surgeon: npis[0].toString(), unit, date: startDateTime }).toString();
			history.replace(`${ROUTE_CONSTANTS.BLOCK_RELEASE_REQUEST}?${manualQueryParam}`);
		} else {
			history.replace(`${route}?${nudgeQueryParam}`);
		}
	}, [history]);

	React.useEffect(() => {
		if (releaseId && selectedReleaseInfo) {
			const scrollTo = document.getElementById(releaseId);
			if (scrollTo) {
				scrollTo.scrollIntoView({ block: 'center', behavior: 'smooth' });
			}
		}
	}, [selectedReleaseInfo, releaseId]);

	if (!recommendedReleases?.length) {
		return null;
	}

	return (
		<>
			{recommendedReleases?.map((recommendedRelease) => {
				const surgeon = surgeonByNpi[recommendedRelease?.npis[0]];
				const surgeonName = getFormattedName(surgeon);

				const timeInterval = {
					start: getDateFromApiDateTime(recommendedRelease.startDateTime),
					end: getDateFromApiDateTime(recommendedRelease.endDateTime),
				};

				return (
					<ReleaseCard
						id={recommendedRelease.id}
						key={recommendedRelease.id}
						className={classes.recommendedCard}
						onClick={() => handleReleaseCardClick(recommendedRelease)}
						surgeonName={surgeonName}
						unit={recommendedRelease?.unit}
						date={recommendedRelease?.startDateTime}
						timeFrame={timeInterval}
						timeZone={timeZoneByHospital[recommendedRelease?.hospitalName]}
						selected={recommendedRelease.id === selectedReleaseInfo?.selectedRelease?.id}
					/>
				);
			})}
		</>
	);
};
